/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages } from 'react-intl'
import { matchPath } from 'react-router-dom'

import { FormattedMessage } from '@modules/i18n'
import { createItem } from '@modules/utils/navigation'
import type { ProjectId } from '@modules/project-admin-api/v1/types'
import { type ProjectType } from '@modules/ui-types/projects'
import { getDisplayProjectId } from '@modules/project-lib/display'
import {
  projectApiConsoleUrl,
  projectCpsUrl,
  projectUrl,
  projectsUrl,
} from '@modules/project-admin-lib/urls'
import { useGetProjectQuery } from '@modules/project-admin-lib/hooks/get'
import { isProjectType } from '@modules/project-creation-lib/types'
import { startingWith } from '@modules/routing/startingWith'
import Feature from '@modules/utils/feature'

import { isFeatureActivated } from '@/store' // eslint-disable-line import/no-restricted-paths

const messages = defineMessages({
  projects: {
    id: `navigation.projects`,
    defaultMessage: `Serverless projects`,
  },
  apiConsole: {
    id: `navigation.projects.apiConsole`,
    defaultMessage: `API Console`,
  },
  cps: {
    id: `navigation.projects.cps`,
    defaultMessage: `Cross-project search`,
  },
})

const ProjectName: React.FunctionComponent<{ projectType: ProjectType; projectId: ProjectId }> = ({
  projectType,
  projectId,
}) => {
  const { data } = useGetProjectQuery(projectType, projectId)
  return <span>{data?.name || getDisplayProjectId(projectId)}</span>
}

export function buildProjectsMenu(routingInfo) {
  const { formatMessage, location } = routingInfo

  const projectsItem = createItem({
    'data-test-id': `projects-link`,
    name: formatMessage(messages.projects),
    href: projectsUrl(),
    items: [],
  })

  const match = matchPath(startingWith(projectUrl(':projectType', ':projectId')), location.pathname)

  if (match && isProjectType(match.params.projectType) && match.params.projectId) {
    const {
      params: { projectType, projectId },
    } = match

    projectsItem.items = [
      createItem({
        'data-test-id': `project-link`,
        name: <ProjectName projectType={projectType} projectId={projectId} />,
        href: projectUrl(projectType, projectId),
        css: {
          a: { width: 'auto' },
        },
        items: [
          createItem({
            'data-test-id': `project-api-console-link`,
            name: <FormattedMessage {...messages.apiConsole} />,
            href: projectApiConsoleUrl(projectType, projectId),
          }),
          ...(isFeatureActivated(Feature.cps)
            ? [
                createItem({
                  'data-test-id': `project-cps-link`,
                  name: <FormattedMessage {...messages.cps} />,
                  href: projectCpsUrl(projectType, projectId),
                }),
              ]
            : []),
        ],
      }),
    ]
  }

  return projectsItem
}
